import { useContext, useEffect } from "react";
import ActionIcon from "../ui/action-icon";
import { SquarePen, ArrowLeft, X } from "lucide-react";
import Link from "next/link";
import SideBarContext from "../../contexts/side-bar-context";
import {
  format,
  isToday,
  isYesterday,
  subDays,
  isWithinInterval,
} from "date-fns";
import { getCommandKey } from "../../lib/utils";
import { useRouter } from "next/router";

const ChatHistoryDrawer = () => {
  const {
    isChatHistory,
    isSideBarOpen,
    toggleChatHistory,
    setIsCanvasOpen,
    chats,
  } = useContext(SideBarContext);

  const router = useRouter();

  const getChatDateCategory = (chatDate) => {
    const updatedAt = new Date(chatDate);
    const today = new Date();
    const currentYear = today.getFullYear();
    const createdYear = updatedAt.getFullYear();

    if (isToday(updatedAt)) return "Today";
    if (isYesterday(updatedAt)) return "Yesterday";
    if (isWithinInterval(updatedAt, { start: subDays(today, 7), end: today }))
      return "Last 7 Days";
    if (
      isWithinInterval(updatedAt, {
        start: subDays(today, 30),
        end: subDays(today, 7),
      })
    )
      return "Last 30 Days";
    if (createdYear === currentYear) {
      return format(updatedAt, "MMMM");
    }

    return format(updatedAt, "MMMM yyyy");
  };

  const groupedChats = chats.reduce((acc, chat) => {
    const category = getChatDateCategory(chat.updated_at);
    if (!acc[category]) acc[category] = [];
    acc[category].push(chat);
    return acc;
  }, {});

  return (
    <div
      className={`${
        isChatHistory
          ? "max-w-full lg:max-w-[240px] lg:min-w-[240px] w-full border-x-[1px] custom-border-grey-50 "
          : "lg:max-w-0 lg:w-0 lg:min-w-0 -translate-x-full lg:translate-x-0 z-[0] overflow-hidden hidden lg:grid"
      } h-full custom-bg-white-200 grid gap-[29px] content-start transition-all py-[27px]  duration-300 transform absolute lg:relative z-30 lg:z-20  
      ${isSideBarOpen ? "lg:z-[0]" : ""}`}
    >
      <div className={` flex items-center justify-between w-full px-[14px]`}>
        <ActionIcon
          position={"right"}
          command={`${getCommandKey("⌘", "Ctrl")} + H`}
          content={"Close chat history"}
          onClick={toggleChatHistory}
          color="custom-text-grey-800"
        >
          <div className="hidden lg:block">
            <X size={18} color="currentColor" />
          </div>
          <div className="lg:hidden block">
            <ArrowLeft size={18} color="currentColor" />
          </div>
        </ActionIcon>
        <Link href="/chats/new">
          <ActionIcon
            onClick={() => {
              if (window.innerWidth < 1024) {
                toggleChatHistory();
              }
            }}
            content="New chat"
            position="right"
            color="custom-text-grey-800"
          >
            <SquarePen size={18} color="currentColor" />
          </ActionIcon>
        </Link>
      </div>
      <div className="w-full grid gap-[16px] px-[14px] content-start">
        <p className="custom-text-grey-900 text-[18px] poppins-bold">History</p>
        <div className="max-h-[85vh] overflow-y-scroll scrollbar-none grid gap-[8px]">
          {Object.entries(groupedChats).map(([category, categoryChats]) => (
            <div
              key={category}
              className="grid gap-[6px] border-b-[1px] custom-border-grey-50 pb-[4px] content-start"
            >
              <p className="custom-text-grey-400 poppins-regular text-[16px] leading-[21px] truncate">
                {category}
              </p>
              {categoryChats.map((chat) => {
                const isActive = router.asPath === `/chats/${chat.id}`;
                return (
                  <Link
                    key={chat.id}
                    href={`/chats/${chat.id}`}
                    onClick={() => {
                      if (window.innerWidth < 1024) {
                        toggleChatHistory();
                      }
                      setIsCanvasOpen(false);
                    }}
                    className={`group lg:py-[7px] md:py-[10px] py-[7px] px-[8px] rounded-[6px] overflow-hidden ${
                      isActive ? "custom-bg-grey-50" : "hover:custom-bg-grey-50"
                    } poppins-medium custom-text-grey-500 text-[14px] leading-[17.64px] whitespace-nowrap overflow-x-hidden max-w-full lg:max-w-[250px] flex items-center justify-between`}
                  >
                    <span className="truncate first-letter-capitalize">
                      {chat.title ? chat.title : "New chat"}
                    </span>
                    {/* <ActionIcon
                        color={`${
                          isActive ? "block" : "hidden group-hover:block"
                        } custom-text-grey-800`}
                      >
                        <Ellipsis size={16} color="currentColor" />
                      </ActionIcon> */}
                  </Link>
                );
              })}
            </div>
          ))}
          {Object.keys(groupedChats).length === 0 && (
            <div className="flex items-center justify-center h-full">
              <p className="custom-text-grey-400 poppins-regular text-[14px]">
                No chat available
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatHistoryDrawer;
